import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'

import { Controller } from 'stimulus'
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import Tus from '@uppy/tus'
import Portuguese from '@uppy/locales/lib/pt_BR'
import { FetchRequest } from '@rails/request.js'
import Swal from 'sweetalert2'

// Uploads the videos of a lesson straight from the browser to the hosting provider, one ticket per
// file. The app only issues the tickets (the provider token never reaches the browser); each file
// streams with tus to its ticket URL and the app is told when it is complete, re-rendering the video
// list by turbo-stream. Only the list is replaced, so this controller and a batch in progress survive.
// The list rows also use it to rename a video inline.
export default class extends Controller {
  static targets = ['trigger', 'status', 'progress', 'percent']
  static values = { createUrl: String, videoableType: String, videoableId: Number }

  connect() {
    // A panel without the upload trigger (video hosting not configured) has nothing to upload.
    if (!this.hasTriggerTarget) return

    this.triggerTarget.addEventListener('click', this.preventDefault)

    this.uppy = new Uppy({
      locale: Portuguese,
      autoProceed: true,
      allowMultipleUploads: true,
      restrictions: { allowedFileTypes: ['video/*'] }
    })

    this.uppy.use(Dashboard, { trigger: this.triggerTarget, closeAfterFinish: true })
    this.uppy.use(Tus, { chunkSize: 64 * 1024 * 1024, retryDelays: [0, 1000, 3000, 5000, 10000], limit: 1 })

    this.uppy.addPreProcessor(this.requestUploadTickets)
    this.uppy.on('upload', ({ fileIDs }) => this.startBatch(fileIDs.length))
    this.uppy.on('progress', (percent) => this.showProgress(percent))
    this.uppy.on('upload-success', (file) => this.completeUpload(file))
    this.uppy.on('complete', () => this.finishBatch())
  }

  disconnect() {
    if (this.hasTriggerTarget) this.triggerTarget.removeEventListener('click', this.preventDefault)
    if (this.uppy) this.uppy.close()
    this.uppy = null
  }

  preventDefault(event) {
    event.preventDefault()
  }

  // Runs before the tus upload: each file gets its own ticket (one request at a time, so the
  // videos are positioned in the order they were selected) and uploads to the ticket URL.
  requestUploadTickets = async (fileIDs) => {
    for (const fileID of fileIDs) {
      const file = this.uppy.getFile(fileID)
      const ticket = await this.requestUploadTicket(file)

      this.uppy.setFileState(fileID, {
        tus: { uploadUrl: ticket.upload_url },
        meta: { ...file.meta, completeUrl: ticket.complete_url }
      })
    }
  }

  async requestUploadTicket(file) {
    const request = new FetchRequest('post', this.createUrlValue, {
      body: JSON.stringify({
        lesson_video: {
          videoable_type: this.videoableTypeValue,
          videoable_id: this.videoableIdValue,
          name: file.name,
          size: file.size
        }
      }),
      responseKind: 'json',
      contentType: 'application/json'
    })
    const response = await request.perform()

    if (!response.ok) {
      const message = await this.errorMessage(response)
      Swal.fire({ icon: 'error', text: message })
      throw new Error(message)
    }

    const ticket = await response.json
    if (ticket.protocol !== 'tus') throw new Error(`Unsupported upload protocol: ${ticket.protocol}`)

    return ticket
  }

  async errorMessage(response) {
    try {
      const body = await response.json
      // The provider detail (e.g. a token missing a scope) tells the admin what to fix.
      return body.error.detail ? `${body.error.message} (${body.error.detail})` : body.error.message
    } catch (_parseError) {
      return `HTTP ${response.statusCode}`
    }
  }

  startBatch(total) {
    this.batchTotal = total
    this.batchDone = 0
  }

  // Batch progress: files done over files selected plus the overall percentage (the Dashboard shows each file).
  showProgress(percent) {
    if (!this.hasStatusTarget) return

    this.statusTarget.classList.remove('hidden')
    if (this.hasProgressTarget) this.progressTarget.value = percent
    if (this.hasPercentTarget) this.percentTarget.textContent = `${this.batchDone}/${this.batchTotal} · ${percent}%`
  }

  finishBatch() {
    if (this.hasStatusTarget) this.statusTarget.classList.add('hidden')
  }

  // The turbo-stream answer replaces the video list only; the upload of the other files goes on.
  completeUpload(file) {
    this.batchDone += 1
    return new FetchRequest('patch', file.meta.completeUrl, { responseKind: 'turbo-stream' }).perform()
  }

  // Inline rename from a list row. Enter must not submit the surrounding lesson form; an unchanged
  // or blank title is not sent.
  async rename(event) {
    if (event.type === 'keydown') event.preventDefault()

    const input = event.currentTarget
    const title = input.value.trim()
    if (title === '' || title === input.defaultValue) return

    input.defaultValue = title
    await new FetchRequest('patch', event.params.url, {
      body: JSON.stringify({ lesson_video: { title } }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    }).perform()
  }
}
